import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';
import { supabase } from '@/lib/supabase';

export interface UnderperformingClass {
  id: string;
  class_name: string;
  attendance_rate: number;
  enrollment_count: number;
  avg_satisfaction: number;
  issues: string[];
}

export interface ClassPerformanceData {
  underperforming_classes: UnderperformingClass[];
  count: number;
}

export const useClassPerformance = () => {
  const { user } = useAuth();
  const errorHandler = useErrorHandler('useClassPerformance');
  
  const organizationId = useMemo(() => {
    return user?.user_metadata?.organization_id as string | undefined;
  }, [user?.user_metadata?.organization_id]);

  return useQuery({
    queryKey: ['classPerformance', organizationId] as const,
    queryFn: async (): Promise<ClassPerformanceData> => {
      if (!organizationId) {
        throw errorHandler.handleError(
          new Error('No organization ID found'),
          'fetchClassPerformance',
          { userId: user?.id },
          false
        );
      }

      try {
        console.log('🔍 useClassPerformance: Starting fetchClassPerformance for org:', organizationId);
        
        // Get all classes with enrollment data using direct supabase calls
        // First get all classes, then get enrollments separately to avoid join issues
        const { data: classes, error: classesError } = await supabase
          .from('classes')
          .select('id, name')
          .eq('organization_id', organizationId)
          .eq('is_active', true);

        if (classesError) {
          console.error('❌ useClassPerformance: Classes query error:', classesError);
          throw classesError;
        }

        console.log('✅ useClassPerformance: Found', classes?.length || 0, 'classes');

        if (!classes || classes.length === 0) {
          return { underperforming_classes: [], count: 0 };
        }

        // Get enrollments for all classes
        const classIds = classes.map(c => c.id);
        const { data: enrollments, error: enrollmentsError } = await supabase
          .from('enrollments')
          .select('id, student_id, status, class_id')
          .in('class_id', classIds)
          .eq('status', 'active');

        if (enrollmentsError) {
          console.error('❌ useClassPerformance: Enrollments query error:', enrollmentsError);
          throw enrollmentsError;
        }

        console.log('✅ useClassPerformance: Found', enrollments?.length || 0, 'enrollments');

        // Get attendance data for the last 2 months
        const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        const twoMonthsAgoStr = twoMonthsAgo.toISOString().split('T')[0];

        const underperformingClasses: UnderperformingClass[] = [];        for (const classData of classes) {
          const classEnrollments = enrollments?.filter(e => e.class_id === classData.id) || [];
          const activeEnrollments = classEnrollments.filter(e => e.status === 'active');

          if (activeEnrollments.length === 0) continue; // Skip classes with no active enrollments

          // Get attendance records for this class using direct supabase call
          const studentIds = activeEnrollments.map(e => e.student_id);
          const { data: attendanceRecords, error: attendanceError } = await supabase
            .from('class_attendance')
            .select('student_id, status, date')
            .eq('organization_id', organizationId)
            .in('student_id', studentIds)
            .gte('date', twoMonthsAgoStr);

          if (attendanceError) continue; // Skip this class if error

          // Calculate attendance rate for this class
          const totalAttendanceRecords = attendanceRecords?.length || 0;
          const actualAttendances = attendanceRecords?.filter(
            record => record.status === 'present' || record.status === 'late'
          ).length || 0;

          const attendanceRate = totalAttendanceRecords > 0 
            ? Math.round((actualAttendances / totalAttendanceRecords) * 100)
            : 100; // Default to 100% if no attendance data

          // Consider underperforming if attendance < 80% or very small enrollment
          const issues: string[] = [];
          if (attendanceRate < 80) issues.push('Low attendance');
          if (activeEnrollments.length < 3) issues.push('Low enrollment');

          if (issues.length > 0) {
            underperformingClasses.push({
              id: classData.id,
              class_name: classData.name || 'Unknown Class',
              attendance_rate: attendanceRate,
              enrollment_count: activeEnrollments.length,
              avg_satisfaction: 3.5, // Default value - would need surveys table
              issues: issues,
            });
          }
        }

        // Sort by attendance rate (lowest first)
        underperformingClasses.sort((a, b) => a.attendance_rate - b.attendance_rate);

        return {
          underperforming_classes: underperformingClasses,
          count: underperformingClasses.length,
        };
      } catch (error) {
        console.error('❌ useClassPerformance: Error in fetchClassPerformance:', error);
        
        // Return safe defaults instead of throwing to prevent app crashes
        return {
          underperforming_classes: [],
          count: 0,
        };
      }
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000,
    retry: 1, // Reduce retries to prevent error spam
    refetchOnWindowFocus: false,
    enabled: !!organizationId,
  });
};